/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { toast } from 'react-toastify'
import ReactSelect from 'react-select'

import { TableWrapper } from 'components/atoms/TableWrapper'
import DashboardContent from '../atoms/DashboardContent'
import Button from 'components/atoms/Button'
import { fullName, physicianFormalName } from 'helpers/nameHelper'
import routes from '../constants/routes'
import Link from 'components/atoms/Link'
import Checkbox from 'components/atoms/IndeterminateCheckbox'
import { confirm } from 'components/molecules/Confirmation'
import ReportPreferenceOptions from 'components/molecules/ReportPreferenceOptions'
import PhysicianNotes from './PhysicianNotes'
import {
  useCreateLabPhysicianNoteMutation,
  useDeleteLabPhysicianMutation,
  useDeleteLabPhysicianNoteMutation,
  useGeneratePatientStatusReportMutation,
  useGetLabPhysicianNotesQuery,
  useGetLabPhysicianPatientsQuery,
  useGetLabPhysicianQuery,
  useUpdateLabPhysicianAddressesMutation,
  useUpdateLabPhysicianMutation,
  useUpdateLabPhysicianNoteMutation,
  useUpdateLabPhysicianSettingsMutation,
} from 'store/services/labPhysician'
import {
  useAddPhysicianGroupMemberMutation,
  useGetPhysicianGroupsQuery,
  useRemovePhysicianGroupMemberMutation,
  useUpdatePhysicianGroupMemberSettingsMutation,
} from 'store/services/physician'
import DataTable from 'components/atoms/DataTable'
import { DeleteDialog } from './DeleteDialog'
import { UpdateAddressDialog } from './UpdateAddressDialog'
import { FollowUpProvidersSection } from './FollowUpProvidersSection'
import { PreferredDmeOfficeSection } from './PreferredDmeOfficeSection'
import { PhysicianModal } from './PhysicianModal'
import { GeneratePatientStatusReportModal } from './GeneratePatientStatusReportModal'
import { KeyVal, Note, PhysicianType } from 'store/types'
import { isOrganization } from 'helpers/followUpProvidersHelper'
import states from 'constants/states'

const StyledTableWrapper = styled(TableWrapper)`
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.2rem;
    div {
      span {
        font-size: 0.9rem;
      }
    }
  }
`

const InfoPanel = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  min-height: 100%;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`

const InfoPanelHeader = styled.div`
  border-bottom: 1px solid #edf2f7;
  color: #2d3748;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 0.65rem 0.85rem;
  text-transform: uppercase;
`

const InfoPanelBody = styled.div`
  padding: 0.85rem;
`

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.25rem 0;

  span {
    color: #718096;
    white-space: nowrap;
  }

  strong {
    color: #1a202c;
    text-align: right;
  }
`

const PhysicianDetails = () => {
  const { npi = '' } = useParams()
  const navigate = useNavigate()
  const [formDirty, setFormDirty] = useState(false)
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(0)
  const [sortOptions, setSortOptions] = useState({})
  const [pageSizeAddresses, setPageSizeAddresses] = useState(20)
  const [currentPageAddresses, setCurrentPageAddresses] = useState(0)
  const [sortOptionsAddresses, setSortOptionsAddresses] = useState({})
  const { data: physician, isLoading, refetch } = useGetLabPhysicianQuery({ npi })
  const { data: notes = [], refetch: refetchNotes } = useGetLabPhysicianNotesQuery(
    { npi },
    { skip: !npi },
  )
  const [updateLabPhysicianSettings] = useUpdateLabPhysicianSettingsMutation()
  const [updateLabPhysicianAddresses] = useUpdateLabPhysicianAddressesMutation()
  const [deleteLabPhysician] = useDeleteLabPhysicianMutation()
  const [updateLabPhysician] = useUpdateLabPhysicianMutation()
  const [createLabPhysicianNote] = useCreateLabPhysicianNoteMutation()
  const [deleteLabPhysicianNote] = useDeleteLabPhysicianNoteMutation()
  const [updateLabPhysicianNote] = useUpdateLabPhysicianNoteMutation()
  const [generatePatientStatusReport, { isLoading: isGeneratingReport }] =
    useGeneratePatientStatusReportMutation()

  const isFollowUpAvailable = typeof physician?.isFollowUpCareEnabled === 'boolean'
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<string | boolean>(false)
  const [addressDialogOpen, setAddressDialogOpen] = useState<number | boolean>(false)
  const [updatePhysicianModalOpen, setUpdatePhysicianModalOpen] = useState(false)
  const [generateReportModalOpen, setGenerateReportModalOpen] = useState(false)
  const [selectedPhysicianGroupId, setSelectedPhysicianGroupId] = useState('')
  const { data: patients, isLoading: isLoadingPatients } = useGetLabPhysicianPatientsQuery({
    npi,
    page: currentPage,
  })
  const { data: physicianGroups = [] } = useGetPhysicianGroupsQuery({})
  const [addPhysicianGroupMember] = useAddPhysicianGroupMemberMutation()
  const [removePhysicianGroupMember] = useRemovePhysicianGroupMemberMutation()
  const [updatePhysicianGroupMemberSettings] = useUpdatePhysicianGroupMemberSettingsMutation()

  const officesWithMoreThanOneAddress = useMemo(
    () =>
      physician?.addresses?.filter(
        (address) =>
          physician?.addresses &&
          physician?.addresses.filter((a) => a.officeId === address.officeId).length > 1,
      ),
    [physician],
  )

  const physicianNotes = useMemo<Note[]>(
    () =>
      notes.map((note) => ({
        ...note,
        pinned: false,
      })),
    [notes],
  )

  const latestPatientStatusReportUrl = physician?.lastPatientStatusReports?.[0]?.url
  const physicianFaxNumber = physician?.addresses?.find((address) => !!address.fax)?.fax || ''
  const physicianGroupOverride = physician?.effectivePreferences?.physicianGroup
  const hasMemberSettingsOverride =
    !!physician?.effectivePreferences?.memberSettingsOverride &&
    Object.keys(physician.effectivePreferences.memberSettingsOverride).length > 0
  const physicianGroupOptions = useMemo(
    () => physicianGroups.map((group) => ({ value: String(group.id), label: group.groupName })),
    [physicianGroups],
  )
  const selectedPhysicianGroup = physicianGroupOptions.find(
    (group) => group.value === selectedPhysicianGroupId,
  )
  const physicianDisplayName = physician
    ? isOrganization(physician.providerType)
      ? physician.organizationName
      : fullName(physician.firstName, physician.lastName)
    : ''
  const preferredDmeOfficeId = physician?.preferredDmeOfficeId ?? null

  const addressesHeader = [
    {
      id: 'select',
      accessor: 'select',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="pl-1 pr-2">
          <Checkbox
            checked={selectedIds.includes(row.original.uuid)}
            onChange={(e) =>
              setSelectedIds((prev) => {
                const uuid = row.original.uuid
                if (prev.includes(uuid)) {
                  return prev?.filter((id) => id !== uuid)
                }
                return [...prev, uuid]
              })
            }
          />
        </div>
      ),
    },
    {
      accessor: 'address',
      Header: 'Address',
      Cell: ({ row }) => {
        const address = [
          row.original.addressStreetLine1,
          row.original.addressStreetLine2,
          row.original.addressCity,
          row.original.addressState,
          row.original.addressZip,
        ]
          .filter(Boolean) // Remove null/undefined values
          .join(', ') // Join with commas

        return <div>{address || '-'}</div> // Show dash if no address components
      },
    },
    {
      accessor: 'office',
      Header: 'Office',
      Cell: ({ row }) => <div>{row.original.officeId ?? '-'}</div>,
    },
    {
      accessor: 'phone',
      Header: 'Phone',
      Cell: ({ row }) => <div>{row.original.telephone}</div>,
    },
    {
      accessor: 'fax',
      Header: 'Fax',
      Cell: ({ row }) => <div>{row.original.fax}</div>,
    },
    {
      accessor: 'taxId',
      Header: 'Tax ID',
      Cell: ({ row }) => <div>{row.original.taxId ?? '-'}</div>,
    },
    {
      id: 'actions',
      Header: '',
      Cell: ({ row }) =>
        officesWithMoreThanOneAddress?.find((a) => a.officeId === row.original.officeId) ? (
          <button
            onClick={() => setDeleteDialogOpen(row.original.uuid)}
            className="btn btn-danger btn-sm"
            aria-label="Delete physician">
            Delete
          </button>
        ) : null,
    },
  ]

  const physicianTypeLabels: Record<string, string> = {
    [PhysicianType.REFERRING]: 'Referring',
    [PhysicianType.FOLLOW_UP]: 'Follow-up',
    [PhysicianType.INTERPRETING]: 'Interpreting',
  }

  const physicianTypeDisplay = (physician?.physicianType || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
    .map((value) => physicianTypeLabels[value] ?? value)
    .join(', ')

  const header = [
    {
      accessor: 'ID',
      Cell: ({ row }) => (
        <Link to={`${routes.index}${routes.patient.root}/${row.original.id}`}>
          {row.original.id}
        </Link>
      ),
      Header: 'ID',
    },
    {
      accessor: 'name',
      Header: 'Name',
      Cell: ({ row }) => (
        <div css={{ position: 'relative' }}>
          <div>
            {physicianFormalName(row.original.firstName, row.original.lastName)}
            {row.original.middleName ? ' ' + row.original.middleName : ''}{' '}
            <Link
              className="open-new-tab"
              css={{ display: 'none', position: 'absolute', right: '0', top: 0 }}
              to={`${routes.index}${routes.physician.root}${routes.physician.upsert}${row.original.npi}`}
              target="_blank"
              icon="fas fa-external-link-alt"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      ),
    },
    {
      accessor: 'office',
      Header: 'Office',
      Cell: ({ row }) => <div>{row.original.officeName}</div>,
    },
  ]

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false)
  }

  const closeAddressDialog = () => {
    setAddressDialogOpen(false)
  }

  const handleDeletePhysician = async (replaceAddressUuid: string) => {
    confirm('physician address')
      .then(async () => {
        if (!physician?.id) return
        const physicianId = physician.addresses?.find(
          (a) => a.uuid === deleteDialogOpen,
        )?.physicianId
        try {
          await deleteLabPhysician({
            physicianId,
            addressUuid: deleteDialogOpen,
            replaceAddressUuid,
          })
          closeDeleteDialog()
          refetch()
        } catch (error) {
          console.error('Error deleting physician:', error)
        }
      })
      .catch(() => {})
  }

  return (
    <DashboardContent
      title="Physician Details"
      icon="fa fa-user-md"
      content={
        <>
          <PhysicianModal
            isOpen={updatePhysicianModalOpen}
            onClose={() => setUpdatePhysicianModalOpen(false)}
            onSuccess={() => {
              setUpdatePhysicianModalOpen(false)
              refetch()
            }}
            physician={physician}
            isUpdateMode={true}
          />
          <DeleteDialog
            addresses={physician?.addresses?.filter(
              (a) =>
                a.uuid !== deleteDialogOpen &&
                a.officeId ===
                  physician?.addresses?.find((a) => a.uuid === deleteDialogOpen)?.officeId,
            )}
            open={!!deleteDialogOpen}
            onClose={closeDeleteDialog}
            onSubmit={handleDeletePhysician}
          />
          <UpdateAddressDialog
            address={physician?.addresses?.find((a) => a.uuid !== deleteDialogOpen)}
            physician={physician}
            open={!!addressDialogOpen}
            onClose={closeAddressDialog}
            onSubmit={(values: KeyVal) =>
              updateLabPhysicianAddresses({
                npi,
                addresses: {
                  addressUuids: selectedIds,
                  ...values,
                },
              }).then(() => {
                closeAddressDialog()
                refetch()
                setSelectedIds([])
              })
            }
          />
          <GeneratePatientStatusReportModal
            isOpen={generateReportModalOpen}
            onClose={() => setGenerateReportModalOpen(false)}
            faxNumber={physicianFaxNumber}
            onGenerate={async (dateRangeStart, dateRangeEnd, shouldFax, faxNumber) => {
              if (!physician?.npi) return
              try {
                await generatePatientStatusReport({
                  npi: physician.npi,
                  dto: {
                    dateRangeStart,
                    dateRangeEnd,
                    shouldFax,
                    faxNumber,
                  },
                }).unwrap()
                setGenerateReportModalOpen(false)
                setTimeout(() => {
                  refetch()
                }, 1000)
                toast.success('Patient status report has been queued for generation!')
              } catch (error) {
                console.error('Error generating patient status report:', error)
                toast.error('Failed to generate patient status report. Please try again.')
              }
            }}
            isGenerating={isGeneratingReport}
          />
          <div className="card card-primary mt-3">
            <div className="row p-3">
              <div className="container-fluid">
                {physician && (
                  <div className="row">
                    <div className="col-sm-12 col-lg-4 mb-3">
                      <InfoPanel>
                        <InfoPanelHeader>Physician Summary</InfoPanelHeader>
                        <InfoPanelBody>
                          <div className="d-flex align-items-center mb-3">
                            <div
                              className="d-flex align-items-center justify-content-center mr-3"
                              css={{
                                width: 44,
                                height: 44,
                                borderRadius: 10,
                                background: physician.id ? '#e6f4ea' : '#edf2f7',
                              }}>
                              {physician.id ? (
                                <i className="text-success fa fa-user-check" />
                              ) : (
                                <i className="text-default fa fa-user-md" />
                              )}
                            </div>
                            <div>
                              <div className="text-muted" css={{ fontSize: 12 }}>
                                NPI {physician.npi}
                              </div>
                              <strong css={{ fontSize: 16 }}>
                                {isOrganization(physician.providerType)
                                  ? physician.organizationName
                                  : fullName(physician.firstName, physician.lastName)}
                              </strong>
                            </div>
                          </div>
                          <DetailRow>
                            <span>Provider Type</span>
                            <strong>
                              {isOrganization(physician.providerType)
                                ? 'Organization'
                                : 'Individual'}
                            </strong>
                          </DetailRow>
                          <DetailRow>
                            <span>Physician Type</span>
                            <strong>{physicianTypeDisplay || '-'}</strong>
                          </DetailRow>
                          <DetailRow>
                            <span>Licensed In</span>
                            <strong>
                              {physician.licensedIn
                                ? physician.licensedIn
                                    .split(',')
                                    .filter(Boolean)
                                    .map((code) => states.find((s) => s.id === code)?.name ?? code)
                                    .join(', ')
                                : '-'}
                            </strong>
                          </DetailRow>
                        </InfoPanelBody>
                      </InfoPanel>
                    </div>

                    <div className="col-sm-12 col-lg-4 mb-3">
                      <InfoPanel>
                        <InfoPanelHeader>Group Assignment</InfoPanelHeader>
                        <InfoPanelBody>
                          {physicianGroupOverride ? (
                            <>
                              <div className="text-muted mb-1" css={{ fontSize: 12 }}>
                                Preferences controlled by
                              </div>
                              <div className="d-flex align-items-center justify-content-between">
                                <Link
                                  to={`${routes.index}${routes.physicianGroups.root}${routes.upsert}${physicianGroupOverride.id}`}
                                  label={physicianGroupOverride.groupName}
                                  style={{ fontWeight: 700 }}
                                />
                                <Button
                                  className="btn btn-outline-danger btn-sm ml-2"
                                  label="Remove"
                                  onClick={async () => {
                                    try {
                                      await removePhysicianGroupMember({
                                        id: Number(physicianGroupOverride.id),
                                        npi: physician.npi,
                                      }).unwrap()
                                      await refetch()
                                      toast.success('Physician removed from group!')
                                    } catch (error: any) {
                                      toast.error(
                                        error?.data?.message ||
                                          'Failed to remove physician from group.',
                                      )
                                    }
                                  }}
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="text-muted mb-2" css={{ fontSize: 12 }}>
                                Assign this physician to a group
                              </div>
                              <div className="d-flex">
                                <div css={{ flex: 1 }}>
                                  <ReactSelect
                                    placeholder="Search physician groups..."
                                    menuPortalTarget={document.body}
                                    options={physicianGroupOptions}
                                    value={selectedPhysicianGroup || null}
                                    onChange={(option) =>
                                      setSelectedPhysicianGroupId(option?.value || '')
                                    }
                                    styles={{
                                      menuPortal: (base) => ({ ...base, zIndex: 10000 }),
                                    }}
                                  />
                                </div>
                                <Button
                                  className="btn btn-primary btn-sm ml-2"
                                  label="Add"
                                  disabled={!selectedPhysicianGroupId}
                                  onClick={async () => {
                                    if (!selectedPhysicianGroupId || !physician.npi) return

                                    try {
                                      await addPhysicianGroupMember({
                                        id: Number(selectedPhysicianGroupId),
                                        npi: physician.npi,
                                        name: physicianDisplayName || physician.npi,
                                      }).unwrap()
                                      setSelectedPhysicianGroupId('')
                                      await refetch()
                                      toast.success('Physician added to group!')
                                    } catch (error: any) {
                                      toast.error(
                                        error?.data?.message || 'Failed to add physician to group.',
                                      )
                                    }
                                  }}
                                />
                              </div>
                            </>
                          )}

                          {isFollowUpAvailable && (
                            <div className="mt-3 pt-3 border-top">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="isFollowUpCareEnabled"
                                  checked={physician.isFollowUpCareEnabled || false}
                                  onChange={(e) => {
                                    updateLabPhysician({
                                      npi: physician.npi,
                                      isFollowUpCareEnabled: e.target.checked,
                                    }).then(() => refetch())
                                  }}
                                  style={{ top: '-2px' }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="isFollowUpCareEnabled"
                                  css={{ fontSize: '14px' }}>
                                  Follow-up Care Provider
                                </label>
                              </div>
                            </div>
                          )}
                        </InfoPanelBody>
                      </InfoPanel>
                    </div>

                    <div className="col-sm-12 col-lg-4 mb-3">
                      <InfoPanel>
                        <InfoPanelHeader>Report Preferences</InfoPanelHeader>
                        <InfoPanelBody>
                          {physicianGroupOverride && (
                            <div className="alert alert-info py-2 mb-2" css={{ fontSize: 13 }}>
                              Controlled by {physicianGroupOverride.groupName}
                              {hasMemberSettingsOverride
                                ? ' with physician-specific overrides'
                                : ''}
                            </div>
                          )}
                          <div
                            style={
                              !physician.officeId
                                ? { cursor: 'not-allowed', opacity: 0.5, pointerEvents: 'none' }
                                : {}
                            }>
                            <ReportPreferenceOptions
                              value={physician.settings?.[0].settings}
                              onChange={(settings) => {
                                if (physicianGroupOverride) {
                                  return updatePhysicianGroupMemberSettings({
                                    id: Number(physicianGroupOverride.id),
                                    npi: physician.npi,
                                    settingsOverride: settings,
                                  }).then(() => refetch())
                                }

                                return updateLabPhysicianSettings({
                                  npi: physician.npi,
                                  settings,
                                }).then(() => refetch())
                              }}
                            />
                          </div>
                          {physicianGroupOverride && (
                            <div className="mt-3 d-flex align-items-center justify-content-between">
                              <span className="text-muted" css={{ fontSize: 12 }}>
                                Changes here only apply to this physician.
                              </span>
                              <Button
                                className="btn btn-outline-secondary btn-sm ml-2"
                                label="Clear Overrides"
                                disabled={!hasMemberSettingsOverride}
                                onClick={async () => {
                                  await updatePhysicianGroupMemberSettings({
                                    id: Number(physicianGroupOverride.id),
                                    npi: physician.npi,
                                    settingsOverride: null,
                                    preferredDeviceTypesOverride: null,
                                  }).unwrap()
                                  await refetch()
                                  toast.success('Physician group overrides cleared!')
                                }}
                              />
                            </div>
                          )}
                        </InfoPanelBody>
                      </InfoPanel>
                    </div>

                    <div className="col-sm-12 d-flex justify-content-start mt-1">
                      <Button
                        className="btn btn-primary"
                        label="Update Physician"
                        onClick={() => setUpdatePhysicianModalOpen(true)}
                      />
                      {physician.settings?.[0]?.settings?.weeklyFaxReport && (
                        <Button
                          className="btn btn-primary ml-2"
                          label="Generate Patient Status Report"
                          onClick={() => setGenerateReportModalOpen(true)}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="row px-3 pt-3" css={{ width: '100%' }}>
                <h4 className="ml-2">Addresses</h4>
                <div className="col-sm-12">
                  <StyledTableWrapper
                    className="card-body row"
                    css={{
                      '& [role=row]:hover': {
                        '& .open-new-tab': {
                          display: 'block',
                        },
                      },
                    }}>
                    <DataTable
                      tableKey={'lab_physician_addresses'}
                      data={{ items: physician?.addresses }}
                      status={isLoading}
                      tableColumns={addressesHeader}
                      pageSize={pageSizeAddresses}
                      setPageSize={setPageSizeAddresses}
                      setCurrentPage={setCurrentPageAddresses}
                      currentPage={currentPageAddresses}
                      setSortOptions={setSortOptionsAddresses}
                      entityName="addresses"
                      onRowClick={(row) => {}}
                    />
                  </StyledTableWrapper>
                </div>
              </div>
              <div className="col-sm-12 d-flex justify-content-start">
                <Button
                  className="btn btn-primary"
                  label="Update Addresses"
                  disabled={!selectedIds.length}
                  onClick={() => setAddressDialogOpen(selectedIds[0])}
                />
              </div>
            </div>
          </div>
          <div className="card card-primary mt-3">
            <div className="row p-3">
              <div className="col-sm-12">
                <h4 className="ml-2">Notes</h4>
              </div>
              <div className="col-sm-12 row">
                <PhysicianNotes
                  userContext="LAB"
                  notes={physicianNotes}
                  addNote={async (text) => {
                    await createLabPhysicianNote({
                      npi: npi,
                      dto: { text },
                    }).unwrap()
                    await refetchNotes()
                  }}
                  deleteNote={async ({ uuid }) => {
                    await deleteLabPhysicianNote({ npi, uuid }).unwrap()
                    await refetchNotes()
                  }}
                  updateNote={async ({ uuid, text }) => {
                    await updateLabPhysicianNote({
                      npi,
                      uuid,
                      dto: { text },
                    }).unwrap()
                    await refetchNotes()
                  }}
                />
              </div>
            </div>
          </div>
          <div className="card card-primary mt-3">
            <div className="row p-3">
              <div className="col-sm-12 d-flex justify-content-between align-items-center">
                <h4 className="ml-2">Patients</h4>
                {latestPatientStatusReportUrl && (
                  <a
                    className="mr-3"
                    href={latestPatientStatusReportUrl}
                    target="_blank"
                    rel="noopener noreferrer">
                    Latest Patient Status Report
                  </a>
                )}
              </div>
              <div className="col-sm-12">
                <StyledTableWrapper
                  className="card-body row"
                  css={{
                    '& [role=row]:hover': {
                      '& .open-new-tab': {
                        display: 'block',
                      },
                    },
                  }}>
                  <DataTable
                    select
                    tableKey={'lab_physician_patients'}
                    data={patients}
                    status={isLoadingPatients}
                    tableColumns={header}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    setSortOptions={setSortOptions}
                    entityName="physician"
                    onRowClick={(row) => {
                      navigate(`${routes.index}${routes.patient.root}/${row.original.id}`)
                    }}
                  />
                </StyledTableWrapper>
              </div>
            </div>
          </div>
          {physician?.npi && (
            <PreferredDmeOfficeSection
              physicianNpi={physician.npi}
              preferredDmeOfficeId={preferredDmeOfficeId}
              onSaved={refetch}
            />
          )}
          {physician?.npi && !isFollowUpAvailable && (
            <FollowUpProvidersSection
              physicianNpi={physician.npi}
              defaultFollowUpPhysicianId={physician.defaultFollowUpPhysicianId}
              onDefaultChanged={refetch}
            />
          )}
        </>
      }
      breadcrumb={[
        {
          label: 'Physicians',
          to: `${routes.index}${routes.physician.root}`,
        },
        {
          label: isOrganization(physician?.providerType)
            ? physician?.organizationName || ''
            : physicianFormalName(physician?.firstName, physician?.lastName, physician?.credential),
        },
      ]}
    />
  )
}

export default PhysicianDetails
